import { toPercent, toDecPercent } from '@utils/Format'
import { optionsFormatData } from '@utils/GraficoData'

// ------------------------- Funções auxiliares -------------------------
const drawLabel = (ctx, dataset, x, y) => {
  ctx.save()
  ctx.font = '12px Arial'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 3
  ctx.strokeText(dataset.label, x, y)
  ctx.fillStyle = dataset.borderColor || '#000'
  ctx.fillText(dataset.label, x, y)
  ctx.restore()
}

const adjustY = (baseY, taken, gap) => {
  let y = baseY
  let up = true
  let tries = 0
  while (taken.some((t) => Math.abs(t - y) < gap) && tries < 20) {
    tries++
    y = baseY + (up ? gap * tries : -gap * tries)
    up = !up
  }
  return y
}

const getCtxOptions = (chart) => ({
  mode: (chart.config.options && chart.config.options.__dadosMode) || 'R$',
  currency: (chart.config.options && chart.config.options.__currency) || 'real',
  formatData:
    (chart.config.options && chart.config.options.__formatData) || ((v) => v),
})

const getGeom = (bar) => {
  const m = bar && (bar._model || bar._view)
  if (!m) return null
  return {
    m,
    base: m.base,
    end: m.x,
    y: m.y,
    height: m.height || 0,
  }
}

export const buildText = (dataset, idx, mode, currency, formatData, raw) => {
  if (raw == null) return null
  const displayRaw = Array.isArray(dataset.rawValues)
    ? dataset.rawValues[idx]
    : raw
  const { formatValuesY } = optionsFormatData(dataset.mode || mode, currency)
  const valueText = formatValuesY(formatData(displayRaw))
  const pctVal = Array.isArray(dataset.porcentagens)
    ? dataset.porcentagens[idx]
    : null
  const pctText = formatPct(pctVal)
  return pctText ? `${pctText} - ${valueText}` : valueText
}

const computePlacement = (ctx, geom, text, labelColor, area) => {
  ctx.font = '600 12px "Proxima Nova", ProximaNova, Inter, sans-serif'
  const textW = ctx.measureText(text).width

  const baseInnerPad = Math.max(4, Math.min(12, Math.floor(geom.height * 0.2)))
  const innerPad = baseInnerPad + 5
  const outerPad = 6

  const barWidth = Math.abs(geom.end - geom.base)
  const goesRight = geom.end >= geom.base
  const fitsInside = textW + innerPad * 2 <= barWidth

  if (fitsInside) {
    return {
      x: goesRight ? geom.end - innerPad : geom.end + innerPad,
      align: goesRight ? 'right' : 'left',
      color: labelColor || '#fff',
    }
  }

  // fora da barra + clamp nos limites
  let x = goesRight ? geom.end + outerPad : geom.end - outerPad
  if (goesRight && x + textW > area.right) x = area.right - textW
  if (!goesRight && x - textW < area.left) x = area.left + textW

  return { x, align: goesRight ? 'left' : 'right', color: '#000' }
}

const drawText = (ctx, text, x, y, align, color) => {
  ctx.save()
  ctx.fillStyle = color
  ctx.textAlign = align
  ctx.textBaseline = 'middle'
  ctx.fillText(text, x, y)
  ctx.restore()
}

const formatPct = (p) =>
  p == null ? null : p >= 0 && p <= 1 ? toDecPercent(p) : toPercent(p)

// ------------------------- Plugins -------------------------
export const getLegendEndOfLinePlugin = () => ({
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx
    const gap = 14
    const offsetX = 10
    const taken = []

    chart.data.datasets.forEach((dataset, i) => {
      if (dataset.type !== 'line') return

      const meta = chart.getDatasetMeta(i)
      if (!meta || !meta.data || !meta.data.length || meta.hidden) return

      const last = meta.data[meta.data.length - 1]
      const m = last && (last._model || last._view)
      if (!m) return

      const x = m.x + offsetX
      const y = adjustY(m.y, taken, gap)
      taken.push(y)

      drawLabel(ctx, dataset, x, y)
    })
  },
})

export const getBarValueLabelsPlugin = () => ({
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx
    const area = chart.chartArea
    const { mode, currency, formatData } = getCtxOptions(chart)

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      if (!meta || meta.hidden) return
      // Datasets marcados (ex.: trilha de fundo) não recebem rótulo de valor.
      if (dataset.hideValueLabel) return

      meta.data.forEach((bar, index) => {
        const geom = getGeom(bar)
        if (!geom) return

        const raw = dataset.data[index]
        const text = buildText(dataset, index, mode, currency, formatData, raw)
        if (!text) return

        const { x, align, color } = computePlacement(
          ctx,
          geom,
          text,
          dataset.labelColor,
          area
        )

        drawText(ctx, text, x, geom.y, align, color)
      })
    })
  },
})
